<template>
  <div class="flex flex-col gap-y-1" :class="{ 'mb-5': !customBottomMargin }">
    <InputLabel v-if="label" :id="id || name" :required="required">
      {{ label }}
    </InputLabel>
    <InputBlocked v-if="blocked" :blockedData="blockedData" @click="emit('blockedLabelClick')" />
    <div v-else class="relative">
      <select
        :id="id || name"
        :name="name"
        class="appearance-none w-full truncate border border-gray-300 hover:border-gray-400 placeholder:text-gray-300 rounded text-gray-600 font-medium text-xs pr-8 shadow-sm focus-within:shadow-blue-100 focus:outline-hidden focus:border-blue-300 focus:ring-1 focus:ring-blue-300 focus:ring-offset-1 focus:ring-offset-blue-200"
        :disabled="disabled"
        :class="[
          compact ? 'h-9 px-3 py-2' : 'h-12 p-3',
          disabled
            ? 'bg-gray-50 cursor-not-allowed'
            : 'bg-white hover:border-gray-400 cursor-pointer',
          {
            'border-red-300 hover:border-red-400': error,
          },
        ]"
        :aria-invalid="!!error"
        :required="required"
        :value="modelValue"
        @change="$event => emit('update:modelValue', $event.target.value)"
      >
        <!-- The first option is selected if no other option is selected and it is disabled when the field is required -->
        <option value="" :selected="!modelValue" :disabled="required">{{ placeholder }}</option>
        <slot name="options">
          <option v-for="option in options" :key="option.id" :value="option.id">
            <slot name="option" :option="option">
              {{ option.name }}
            </slot>
          </option>
        </slot>
      </select>
      <div class="absolute right-3 h-full top-0 flex items-center pointer-events-none">
        <LockClosedIcon
          v-if="disabled"
          :class="compact ? 'w-4 h-4' : 'w-5 h-5'"
          class="bg-gray-50"
        />
        <ChevronUpDownIcon
          v-else
          class="text-gray-500 transform duration-300 bg-white"
          aria-hidden="true"
          :class="compact ? 'w-4 h-4' : 'w-5 h-5'"
        />
      </div>
    </div>
    <ErrorMessage v-if="error && showErrorMessage">{{ error }}</ErrorMessage>
    <HelpMessage v-if="helpText">{{ helpText }}</HelpMessage>
  </div>
</template>

<script setup>
import InputLabel from "./_base/InputLabel.vue"
import InputBlocked from "./_base/InputBlocked.vue"
import ErrorMessage from "./_base/ErrorMessage.vue"
import HelpMessage from "./_base/HelpMessage.vue"
import { ChevronUpDownIcon, LockClosedIcon } from "@heroicons/vue/20/solid"

defineProps({
  name: String,
  id: String,
  placeholder: String,
  modelValue: [String, Number],
  error: [Boolean, String],
  compact: Boolean,
  label: String,
  required: Boolean,
  disabled: Boolean,
  helpText: String,
  customBottomMargin: Boolean,
  options: Array, // [{id: 1, name: option 1}] Both id and name are required,
  blocked: Boolean,
  blockedData: Object,
  showErrorMessage: {
    type: Boolean,
    default: true,
  },
})

const emit = defineEmits(["update:modelValue", "blockedLabelClick"])
</script>

<style>
select::-ms-expand {
  /* removes expand icon in IE10                   */
  display: none;
}
</style>
